import React, { useEffect, useRef, useState } from "react";
import "./index.scss";
import { Table, ConfigProvider, Empty, Tooltip } from 'antd';
import classNames from "classnames";

import CustomTableColumnVisibleFilter from "../CustomTableColumnVisibleFilter";
import CustomerReviewSearchPeriod from "../CustomerReviewSearchPeriod/CustomerReviewSearchPeriod";
import { getOperatorFiltersLabel } from "../../../utils/tableFiltersHelpers";
import { isRTL } from "../../../services/userModel";
import moment from "moment";
import { DATE_TIME_FORMAT } from "../../../utils/convertTime.js";
import IconButton from '../IconButton/index.js';
import SearchBox from '../SearchBox/index.js';
import AutoRefreshIndicator from '../AutoRefreshIndicator/index.js';

const ROW_HEIGHT = 55;

const CustomTable = ({
    t,
    data,
    columns,
    dataColumns,
    loading,
    components,
    columnVisible,
    setColumnVisible,
    filter,
    onSetFilter,
    handleOnSaveReport,
    filters,
    handleRemoveFilter,
    handleRemoveAll,
    handleResetFilters,
    resetFiltersConfig,
    handleAddFilter,
    pagination,
    setPagination,
    setSearchValue,
    searchValue,
    onSelectRow,
    reportComponent,
    addComponent,
    summaryComponent,
    periodControl,
    hideDatePredefinedValue,
    showAutoRefresh,
    isAutoRefresh,
}) => {
    const headerRef = useRef();
    const tableRef = useRef();
    const scrollBoxRef = useRef();
    const [height, setHeight] = useState(500);
    const [selectedRow, setSelectedRow] = useState(null);
    // Mobile only: the filters block is hidden from the header (unusable as a cramped chip line)
    // and opens as a bottom sheet from a funnel button in the fixed footer strip.
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

    // On phones the whole page scrolls and the table takes its natural content height. The
    // desktop height formula assumes .customTable is viewport-locked; with auto-height mobile
    // containers it feeds on its own output and spirals the table height down to negative.
    const isMobileTable = typeof window !== "undefined" && window.matchMedia
        && window.matchMedia("(max-width: 900px)").matches;

    useEffect(() => {
        if (isMobileTable) return undefined;
        const copyref = headerRef.current;
        const updateHeight = () => {
            if (tableRef.current) setHeight(tableRef.current.clientHeight - headerRef.current.clientHeight - ROW_HEIGHT - 30 - 15);
        };
        updateHeight();
        const resizeObserver = new ResizeObserver(entries => {
            updateHeight();
        });

        resizeObserver.observe(copyref);
        return () => {
            resizeObserver.unobserve(copyref);
        };
    }, [isMobileTable]);

    const renderEmpty = (componentName) => {
        return <Empty className="customTableEmpty" image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('orders.NO_DATA')} />;
    };

    const handlePaginationChange = (page, pageSize) => {
        setPagination({ page: page - 1, pageSize, pageOrSortChanged: true });
        if (scrollBoxRef.current) {
            scrollBoxRef.current.scrollTop = 0;
        }
        window.scrollTo(0, 0);
    }

    const handleOnSelectRow = (record) => {
        setSelectedRow(record.Id ?? record.id ?? record.ConsumerId);
        if (onSelectRow) {
            onSelectRow(record);
        }
    }

    const showPeriodReportBlock =  !hideDatePredefinedValue;

    return (
        <div className="customTable" ref={tableRef}>
            <div ref={headerRef} className="customTableHeaderContainer">

                <div className="controlsCustomHeader firstControlsCustomHeader">
                    {showPeriodReportBlock && (
                        <div className="saveAndPeriodBlock">
                            {!hideDatePredefinedValue && (periodControl || (
                                <CustomerReviewSearchPeriod
                                    filter={filter}
                                    onSetFilter={onSetFilter}
                                    title={`${t("orders.PERIOD_HEADER_LABLE")}:`}
                                    isHideYear
                                />
                            ))}
                        </div>
                    )}
                    <div className={'summaryBlockCustomTable'}>
                        <SearchBox placeholder={t("orders.SEARCH_LABEL")} debounce={500} onChange={setSearchValue} useValue={searchValue} />
                    </div>
                    {reportComponent?.()}
                    {addComponent?.()}
                    {showAutoRefresh && (
                        <AutoRefreshIndicator t={t} isAutoRefresh={isAutoRefresh} />
                    )}
                </div>
                <div className={classNames("controlsCustomHeader", "filtersHeader", { mobileOpen: mobileFiltersOpen })}>
                    <div className={classNames("periodTitle")}>
                        <span className="periodTitleLabel">{t("orders.FILTERS__HEADER_LABLE")}: </span>
                        <div className="filtersInfoBlock">
                            {Object.entries(filters)
                                .filter(([accessor, filterObj]) => !!filterObj.type)
                                .filter(([accessor, filterObj]) => !columns.find(c => c.accessor === accessor)?.hideFilterFromPreview).map(([accessor, filterObj]) => {
                                    if (!filterObj) return null;
                                    const value = filterObj.type === "date" && filterObj.value ? moment(filterObj.value).format(DATE_TIME_FORMAT) : filterObj.value;
                                    const value2 = filterObj.type === "date" && filterObj.value2 ? moment(filterObj.value2).format(DATE_TIME_FORMAT) : filterObj.value2;

                                    const column = dataColumns.find((c) => c.accessor === accessor);
                                    if (!column) return null;

                                    if (column?.formatForPreview) {
                                        const { columnTitle, operator, value } = column.formatForPreview(data, filterObj);
                                        if (columnTitle || operator || value) {
                                            return (
                                                <div className={`filterBlock ${Object.entries(filters)?.length > 3 ? 'mt4' : ''}`} key={accessor}>
                                                    <span className="fbCollumn">{columnTitle}</span>
                                                    <span className="fbOperator">{operator ? ` ${operator} ` : " "}</span>
                                                    <span className="fbValue">{value}</span>
                                                    {!column.alwaysOnFilter && (
                                                        <span
                                                            className="removeBlock"
                                                            onClick={() => handleRemoveFilter(accessor)}
                                                        >
                                                            <IconButton isIcon iconName={'crossClose'} />
                                                        </span>
                                                    )}
                                                </div>
                                            )
                                        }
                                    }

                                    const columnTitle = column.title || accessor;
                                    const operatorLabel = getOperatorFiltersLabel(filterObj.operator, t);

                                    let displayValue = "";
                                    if (Array.isArray(value)) {
                                        displayValue = value.map(l => l.value).join(", ");
                                    } else if (filterObj.operator === "between") {
                                        const empty = [null, undefined];
                                        if (empty.includes(value) || empty.includes(value2)) {
                                            displayValue = `${value || value2}`;
                                        } else {
                                            displayValue = `${value} - ${value2}`;
                                        }
                                    } else if ((filterObj.operator === "before" && filterObj.type === "date") || filterObj.operator === "less") {
                                        displayValue = value2;
                                    } else {
                                        displayValue = value;
                                    }
                                    if (filterObj.operator === "any_value") {
                                        displayValue = "";
                                    }
                                    if (filterObj.operator === "empty") {
                                        displayValue = "";
                                    }

                                    return (
                                        <div className={`filterBlock ${Object.entries(filters)?.length > 3 ? 'mt4' : ''}`} key={accessor}>
                                            <span className="fbCollumn">{columnTitle}</span>
                                            <span className="fbOperator">{operatorLabel ? ` ${operatorLabel} ` : " "}</span>
                                            <span className="fbValue">{displayValue}</span>
                                            {!column.alwaysOnFilter && (
                                                <span
                                                    className="removeBlock"
                                                    onClick={() => handleRemoveFilter(accessor)}
                                                >
                                                    <IconButton isIcon iconName={'crossClose'} />
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            <button className={`addBtn ${Object.entries(filters)?.length > 3 ? 'mt4' : ''}`} onClick={handleAddFilter}>
                                +
                            </button>
                            {Object.entries(filters)?.length
                                ? <button className={`removeAllBtn ${Object.entries(filters)?.length > 3 ? 'mt4' : ''}`} onClick={handleRemoveAll}>
                                    {t("orders.REMOVE_ALL_FILTER_BUTTON")}
                                </button>
                                : null}
                            {/* Opt-in per table via customTableConfig.resetFiltersButton. It sits
                                next to "remove all" and does the opposite: that one empties the
                                filters, this one puts back the configured starting point (default
                                chips + the default period of customTableConfig.filterType). Hence
                                the neutral styling — same row, deliberately not a second blue twin. */}
                            {resetFiltersConfig
                                ? <button
                                    type="button"
                                    className={`resetFiltersBtn ${Object.entries(filters)?.length > 3 ? 'mt4' : ''}`}
                                    onClick={handleResetFilters}
                                >
                                    {t(resetFiltersConfig.title || "orders.RESTORE_DEFAULT_FILTERS_BUTTON")}
                                </button>
                                : null}
                        </div>
                    </div>
                    <CustomTableColumnVisibleFilter
                        maxHeight={height}
                        columns={columns}
                        columnVisible={columnVisible}
                        setColumnVisible={setColumnVisible}
                        t={t}
                    />
                </div>
            </div>

            {/* the CARD is the scroll box (desktop: JS-computed height, thead pinned via CSS
                sticky). antd's own scroll.y would render the header as a separate fixed-layout
                table with evenly-stretched columns — this way table-layout stays auto and
                columns fit their content. */}
            <div ref={scrollBoxRef} className="tableContainer" style={isMobileTable ? undefined : { height: `${height}px`, overflow: "auto" }}>
                <ConfigProvider renderEmpty={renderEmpty} direction={isRTL() ? "rtl" : "ltr"}>
                    <Table
                        onRow={(record, rowIndex) => ({
                            onClick: event => handleOnSelectRow(record),
                        })}
                        locale={{
                            triggerDesc: t("orders.TRIGGER_DESC_SORT"),
                            triggerAsc: t("orders.TRIGGER_ASC_SORT"),
                            cancelSort: t("orders.CANCEL_SORT"),
                        }}
                        rowClassName={(record, index) => (record.Id ?? record.id ?? record.ConsumerId) === selectedRow ? 'activeRow' : ''}
                        loading={loading}
                        pagination={{
                            // size: 'small',
                            showLessItems: true,
                            onChange: handlePaginationChange,
                            total: pagination.loading ? (pagination.pageSize * 5) : pagination.totalItems,
                            showSizeChanger: true,
                            pageSizeOptions: [10, 20, 30, 50, 100],
                            defaultPageSize: pagination.pageSize || 30,
                            current: pagination.page + 1,
                        }}
                        // antd sticky is off (the card is the scroll box; the thead is pinned by a
                        // plain CSS position:sticky in orderspage/index.scss)
                        sticky={false}
                        // records carry PascalCase Id (see onRow) — the old rowKey="id" resolved to
                        // undefined for every row, hence the duplicate-key warnings
                        rowKey={(record) => record.Id ?? record.id}
                        columns={dataColumns.map((col) => ({
                            ...col,
                            title: () => <Tooltip title={
                                filters[col.accessor]?.sort === 0 || (col.enableSortColumn && !filters[col.accessor]?.sort)
                                    ? t('orders.TRIGGER_DESC_SORT')
                                    : filters[col.accessor]?.sort === -1
                                        ? t('orders.CANCEL_SORT')
                                        : filters[col.accessor]?.sort === 1
                                            ? t('orders.TRIGGER_ASC_SORT')
                                            : ''
                            }
                            >
                                {col.title}
                                {' '}
                                {filters[col.accessor]?.sort === 1 && "▾"}
                                {filters[col.accessor]?.sort === -1 && "▴"}
                            </Tooltip>,
                        }))}
                        dataSource={data}
                        components={components}
                    />
                </ConfigProvider >
            </div>

            <div className="tableStat">
                {/* Mobile-only (CSS-hidden on desktop): opens the filters block as a bottom sheet.
                    The badge carries the active-filter count so state is visible while collapsed. */}
                <button
                    type="button"
                    className={classNames("tableStatFilterBtn", { open: mobileFiltersOpen })}
                    aria-label={t("orders.FILTERS__HEADER_LABLE")}
                    onClick={() => setMobileFiltersOpen(v => !v)}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M3 5h18l-7 8v5l-4 2v-7L3 5z" stroke="currentColor" strokeWidth="2"
                            strokeLinejoin="round" fill={mobileFiltersOpen ? "currentColor" : "none"} />
                    </svg>
                    {Object.values(filters).filter(f => f && f.type).length > 0 && (
                        <span className="tableStatFilterBadge">
                            {Object.values(filters).filter(f => f && f.type).length}
                        </span>
                    )}
                </button>
                {summaryComponent?.()}
            </div>
        </div>
    );
};

export default CustomTable;
